import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Edit2, Building2, Car, X } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = {
  mecanica: 'Mecânica', pecas: 'Peças', legalizacao: 'Legalização',
  transporte: 'Transporte', limpeza: 'Limpeza', outros: 'Outros',
  renda: 'Renda', eletricidade: 'Eletricidade', internet: 'Internet',
  salarios: 'Salários', contabilidade: 'Contabilidade', seguros: 'Seguros',
  publicidade: 'Publicidade', manutencao: 'Manutenção'
};

const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v || 0);

const AdminExpenses = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Filters
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterCar, setFilterCar] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all'); // all, vehicle, general

  // Form
  const [form, setForm] = useState({
    is_general: false, car_id: '', expense_type: '', description: '', amount: '', date: new Date().toISOString().split('T')[0]
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth !== 'all') params.append('month', filterMonth);
      if (filterCar !== 'all') params.append('car_id', filterCar);
      if (filterCategory === 'general') params.append('is_general', 'true');
      if (filterCategory === 'vehicle') params.append('is_general', 'false');
      if (filterType !== 'all') params.append('expense_type', filterType);

      const [expRes, carRes] = await Promise.all([
        axios.get(`${API_URL}/api/finance/expenses?${params}`, { headers }),
        axios.get(`${API_URL}/api/admin/cars`, { headers })
      ]);
      setExpenses(expRes.data);
      setCars(carRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterCar, filterType, filterCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const vehicleExpenseTypes = ['mecanica', 'pecas', 'legalizacao', 'transporte', 'limpeza', 'outros'];
  const generalExpenseTypes = ['renda', 'eletricidade', 'internet', 'salarios', 'contabilidade', 'seguros', 'publicidade', 'manutencao', 'outros'];

  const resetForm = () => {
    setForm({ is_general: false, car_id: '', expense_type: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.expense_type || !form.amount || !form.date) {
      toast.error('Preencha tipo, valor e data');
      return;
    }
    if (!form.is_general && !form.car_id) {
      toast.error('Selecione uma viatura ou marque como despesa geral');
      return;
    }
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        car_id: form.is_general ? null : form.car_id
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/finance/expenses/${editingId}`, {
          expense_type: payload.expense_type,
          description: payload.description,
          amount: payload.amount,
          date: payload.date
        }, { headers });
        toast.success('Despesa atualizada');
      } else {
        await axios.post(`${API_URL}/api/finance/expenses`, payload, { headers });
        toast.success('Despesa adicionada');
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('Erro ao guardar despesa');
    }
  };

  const handleEdit = (exp) => {
    setForm({
      is_general: exp.is_general,
      car_id: exp.car_id || '',
      expense_type: exp.expense_type,
      description: exp.description,
      amount: String(exp.amount),
      date: exp.date
    });
    setEditingId(exp.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que quer eliminar esta despesa?')) return;
    try {
      await axios.delete(`${API_URL}/api/finance/expenses/${id}`, { headers });
      toast.success('Despesa eliminada');
      fetchData();
    } catch (err) {
      toast.error('Erro ao eliminar');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  const getMonthOptions = () => {
    const opts = [{ value: 'all', label: 'Todos os meses' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
      opts.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return opts;
  };

  const totalFiltered = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin/financas')} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors" data-testid="back-to-finance">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-medium text-white" data-testid="expenses-title">Gestão de Despesas</h1>
            <p className="text-zinc-500">Adicione e gira despesas de viaturas e do negócio</p>
          </div>
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-expense-btn">
            <Plus size={18} className="mr-2" /> Nova Despesa
          </Button>
        </div>

        {/* Form Modal - rendered via Portal to escape AdminLayout container */}
        {showForm && createPortal(
          <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto overflow-x-hidden" onClick={() => resetForm()}>
            <div className="bg-[#12141D] border border-zinc-800 w-full max-w-[calc(100vw-2rem)] sm:max-w-lg mt-20 mb-8" onClick={e => e.stopPropagation()} data-testid="expense-form-modal">
              <div className="flex items-center justify-between p-5 border-b border-zinc-800">
                <h3 className="text-lg font-medium text-white">{editingId ? 'Editar Despesa' : 'Nova Despesa'}</h3>
                <button onClick={resetForm} className="text-zinc-400 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* Category Toggle */}
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm(f => ({ ...f, is_general: false }))}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${!form.is_general ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                    data-testid="expense-type-vehicle">
                    <Car size={16} /> Viatura
                  </button>
                  <button type="button" onClick={() => setForm(f => ({ ...f, is_general: true, car_id: '' }))}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${form.is_general ? 'bg-orange-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
                    data-testid="expense-type-general">
                    <Building2 size={16} /> Geral
                  </button>
                </div>

                {/* Vehicle selector */}
                {!form.is_general && (
                  <div>
                    <label className="text-zinc-400 text-sm mb-1.5 block">Viatura *</label>
                    <Select value={form.car_id} onValueChange={v => setForm(f => ({ ...f, car_id: v }))}>
                      <SelectTrigger className="bg-[#090A0F] border-zinc-800 text-white" data-testid="expense-car-select">
                        <SelectValue placeholder="Selecionar viatura..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#12141D] border-zinc-800 max-h-60">
                        {cars.map(c => (
                          <SelectItem key={c.id} value={c.id} className="text-white hover:bg-zinc-800">
                            {c.brand} {c.model} ({c.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Expense type */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">Tipo de despesa *</label>
                  <Select value={form.expense_type} onValueChange={v => setForm(f => ({ ...f, expense_type: v }))}>
                    <SelectTrigger className="bg-[#090A0F] border-zinc-800 text-white" data-testid="expense-type-select">
                      <SelectValue placeholder="Selecionar tipo..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12141D] border-zinc-800">
                      {(form.is_general ? generalExpenseTypes : vehicleExpenseTypes).map(t => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-zinc-800">{typeLabels[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Amount & Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-400 text-sm mb-1.5 block">Valor (EUR) *</label>
                    <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00" className="bg-[#090A0F] border-zinc-800 text-white" data-testid="expense-amount-input" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1.5 block">Data *</label>
                    <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                      className="bg-[#090A0F] border-zinc-800 text-white" data-testid="expense-date-input" />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">Descrição</label>
                  <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Descrição da despesa..." className="bg-[#090A0F] border-zinc-800 text-white" data-testid="expense-description-input" />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={resetForm} className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700">Cancelar</Button>
                  <Button type="submit" className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid="expense-save-btn">
                    {editingId ? 'Atualizar' : 'Adicionar'}
                  </Button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 bg-[#12141D] border-zinc-800 text-white text-sm" data-testid="filter-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-zinc-800">
              <SelectItem value="all" className="text-white hover:bg-zinc-800">Todas</SelectItem>
              <SelectItem value="vehicle" className="text-white hover:bg-zinc-800">Viaturas</SelectItem>
              <SelectItem value="general" className="text-white hover:bg-zinc-800">Gerais</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-44 bg-[#12141D] border-zinc-800 text-white text-sm" data-testid="filter-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-zinc-800">
              {getMonthOptions().map(o => (
                <SelectItem key={o.value} value={o.value} className="text-white hover:bg-zinc-800">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCar} onValueChange={setFilterCar}>
            <SelectTrigger className="w-52 bg-[#12141D] border-zinc-800 text-white text-sm" data-testid="filter-car">
              <SelectValue placeholder="Viatura" />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-zinc-800 max-h-60">
              <SelectItem value="all" className="text-white hover:bg-zinc-800">Todas viaturas</SelectItem>
              {cars.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-white hover:bg-zinc-800">{c.brand} {c.model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 bg-[#12141D] border-zinc-800 text-white text-sm" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-zinc-800">
              <SelectItem value="all" className="text-white hover:bg-zinc-800">Todos tipos</SelectItem>
              {[...vehicleExpenseTypes, ...generalExpenseTypes.filter(t => t !== 'outros')].map(t => (
                <SelectItem key={t} value={t} className="text-white hover:bg-zinc-800">{typeLabels[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Total */}
        <div className="bg-[#12141D] border border-zinc-800 px-6 py-4 mb-4 flex items-center justify-between">
          <span className="text-zinc-400 text-sm">{expenses.length} despesas encontradas</span>
          <span className="text-white font-semibold text-lg">Total: {fmt(totalFiltered)}</span>
        </div>

        {/* Expenses Table */}
        {loading ? (
          <div className="text-center py-16"><p className="text-zinc-400">A carregar...</p></div>
        ) : (
          <div className="bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="expenses-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-zinc-500">
                    <th className="text-left px-5 py-3 font-medium">Data</th>
                    <th className="text-left px-4 py-3 font-medium">Categoria</th>
                    <th className="text-left px-4 py-3 font-medium">Tipo</th>
                    <th className="text-left px-4 py-3 font-medium">Viatura / Descrição</th>
                    <th className="text-right px-4 py-3 font-medium">Valor</th>
                    <th className="text-center px-4 py-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-zinc-500">Nenhuma despesa encontrada</td></tr>
                  ) : expenses.map(exp => (
                    <tr key={exp.id} className="border-b border-zinc-800/50 hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-3 text-zinc-300">{formatDate(exp.date)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${exp.is_general ? 'bg-orange-600/20 text-orange-400' : 'bg-red-600/20 text-red-400'}`}>
                          {exp.is_general ? 'Geral' : 'Viatura'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">{typeLabels[exp.expense_type] || exp.expense_type}</td>
                      <td className="px-4 py-3">
                        {exp.car_info ? <span className="text-white">{exp.car_info}</span> : null}
                        {exp.description && <span className="text-zinc-500 ml-1">- {exp.description}</span>}
                        {!exp.car_info && !exp.description && <span className="text-zinc-600">-</span>}
                      </td>
                      <td className="text-right px-4 py-3 text-white font-medium">{fmt(exp.amount)}</td>
                      <td className="text-center px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => handleEdit(exp)} className="text-zinc-400 hover:text-white transition-colors" data-testid={`edit-expense-${exp.id}`}>
                            <Edit2 size={15} />
                          </button>
                          <button onClick={() => handleDelete(exp.id)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`delete-expense-${exp.id}`}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminExpenses;
